import { useCallback, useRef } from 'react';

type Align = 'top' | 'bottom';

type UseScrollIntoViewIfNeededParams = {
  align: Align;
  offset?: number;
};

export const useScrollIntoViewIfNeeded = <T extends HTMLElement = HTMLElement>({
  align = 'top',
  offset = 0,
}: UseScrollIntoViewIfNeededParams) => {
  const elementRef = useRef<T | null>(null);
  const timeoutRef = useRef<number | null>(null);
  const observerRef = useRef<ResizeObserver | null>(null);

  const scrollIntoViewIfNeeded = useCallback(() => {
    const element = elementRef.current;

    if (!element) {
      return;
    }

    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    const observer = new ResizeObserver(() => {
      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = window.setTimeout(() => {
        const alignTop = align === 'top';
        const alignBottom = align === 'bottom';
        const { top, bottom } = element.getBoundingClientRect();
        const { innerHeight } = window;
        const rootStyles = getComputedStyle(document.documentElement);
        const tgSafeAreaInsetBottom = parseFloat(
          rootStyles.getPropertyValue('--tg-safe-area-inset-bottom').trim(),
        );

        const isOutOfView =
          (alignTop && top < offset) ||
          (alignBottom &&
            bottom > innerHeight - offset - tgSafeAreaInsetBottom);

        if (isOutOfView) {
          const marginProp = alignTop
            ? 'scrollMarginTop'
            : 'scrollMarginBottom';
          element.style[
            marginProp
          ] = `calc(${offset}px + var(--tg-safe-area-inset-bottom, 0px))`;

          element.scrollIntoView({
            block: alignTop ? 'start' : 'end',
            inline: 'nearest',
            behavior: 'smooth',
          });
        }

        observer.disconnect();
        observerRef.current = null;
        timeoutRef.current = null;
      }, 50);
    });

    observer.observe(document.documentElement);
    observerRef.current = observer;
  }, [elementRef.current, align, offset]);

  return { elementRef, scrollIntoViewIfNeeded };
};
